/**
 * Product analytics (PostHog, EU cloud).
 *
 * Every export is a no-op unless VITE_POSTHOG_KEY is set at build time. Only
 * .env.production carries the key, so local dev and staging never write into
 * the live data — put the key in .env.local to try it locally.
 *
 * The SDK is ~100 KB gzipped, so it is loaded as its own chunk after the app
 * bundle instead of inside it: first paint never waits for analytics. Calls
 * made before it arrives are queued and replayed once it is ready.
 *
 * The widget serves every club from one build, so each event carries the club
 * (saas_id / club_slug) as a persisted super property — data must always be
 * split per club. People are identified by their Apointa user _id only; no
 * name, email or phone is ever sent.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY || "";
const HOST = import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";

// Path segments that carry a secret (one-time tokens, split-hold tokens) and
// query params that do (claim access codes). They are blanked before an event
// leaves the browser — PostHog keeps URLs in plain sight on every event.
const SECRET_PATH_PREFIXES = ["/auth/verify/", "/auth/claim-email/", "/auth/reset-password/", "/checkout/telr-hold/"];
const SECRET_QUERY_PARAMS = ["code", "token"];
const URL_PROPERTIES = ["$current_url", "$pathname", "$referrer", "$initial_current_url", "$initial_pathname", "$initial_referrer"];

// "off" (no key, or the SDK failed to load) → every call is dropped.
// "loading" → calls wait in the queue. "ready" → calls go straight through.
let status = "off";
let posthog = null;
const pending = [];
const MAX_PENDING = 200;

export function redactUrl(value) {
  if (typeof value !== "string" || !value) return value;
  let out = value;
  for (const prefix of SECRET_PATH_PREFIXES) {
    const at = out.indexOf(prefix);
    if (at === -1) continue;
    const start = at + prefix.length;
    const end = out.slice(start).search(/[/?#]/);
    out = `${out.slice(0, start)}:token${end === -1 ? "" : out.slice(start + end)}`;
  }
  for (const param of SECRET_QUERY_PARAMS) {
    out = out.replace(new RegExp(`([?&]${param}=)[^&#]*`, "g"), "$1:redacted");
  }
  return out;
}

// Session replay keeps its own copy of the page URL inside the recording — on
// the page-load meta event and on every "$url_changed" — so those are blanked
// the same way, or a replay of a reset-password visit would show the token.
function redactSnapshot(snapshotData) {
  if (!Array.isArray(snapshotData)) return;
  for (const item of snapshotData) {
    const data = item?.data;
    if (!data) continue;
    if (typeof data.href === "string") data.href = redactUrl(data.href);
    if (typeof data.payload?.href === "string") data.payload.href = redactUrl(data.payload.href);
  }
}

export function redactEvent(event) {
  if (!event) return event;
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue;
    for (const prop of URL_PROPERTIES) {
      if (prop in bag) bag[prop] = redactUrl(bag[prop]);
    }
  }
  if (event.event === "$snapshot") redactSnapshot(event.properties?.$snapshot_data);
  return event;
}

// Attributes that carry readable text — an avatar's alt is the player's name on
// a class roster or an open-game seat. Structural ones (class, style, src) stay,
// or the replay can't be drawn.
const TEXT_ATTRIBUTES = ["alt", "title", "aria-label"];

export function maskTextAttribute(name, value) {
  return TEXT_ATTRIBUTES.includes(name) && value ? "*".repeat(value.length) : value;
}

/**
 * Class for anything clickable that shows a person (family switcher, a trainer's
 * client list). PostHog's click autocapture sends the clicked element's text, so
 * clicks inside it are not autocaptured at all; track an explicit event without
 * the name instead where the click matters. Dialogs and drawers render outside
 * their view, so they need the class themselves.
 */
export const NO_CAPTURE = "ph-no-capture";

// Meta and Google only credit a conversion to an ad when it carries the click
// id the ad appended to the landing URL. Keep the latest one on every later
// event (the purchase can come days and a Telr round trip later); `fbc` is the
// cookie format Meta's Conversions API expects. Read from the landing URL as it
// was before the app started, since the SDK arrives after the first render.
function rememberAdClick(ph, landingSearch) {
  const params = new URLSearchParams(landingSearch);
  const fbclid = params.get("fbclid");
  const gclid = params.get("gclid");
  if (fbclid) ph.register({ fbclid, fbc: `fb.1.${Date.now()}.${fbclid}` });
  if (gclid) ph.register({ gclid });
}

// Tracking must never break the app — several calls sit on the success path of
// a booking, cancel or purchase that has already gone through.
function run(fn) {
  try {
    fn(posthog);
  } catch (_err) {
    // Lost event; the user's action is unaffected.
  }
}

function safely(fn) {
  if (status === "ready") run(fn);
  else if (status === "loading" && pending.length < MAX_PENDING) pending.push(fn);
}

export function initAnalytics() {
  if (status !== "off" || !KEY || typeof window === "undefined") return;
  status = "loading";
  const landingSearch = window.location.search;
  import("posthog-js")
    .then(({ default: ph }) => {
      ph.init(KEY, {
        api_host: HOST,
        // SPA page views on every route change, plus the current recommended defaults.
        defaults: "2026-08-30",
        // Anonymous visitors don't get a person profile until they sign in.
        person_profiles: "identified_only",
        session_recording: {
          maskAllInputs: true,
          // Names, emails and phones are plain text across the widget (account
          // menu, profile, class rosters, open-game seats), so every text node is
          // masked rather than each screen — a new screen can't leak by omission.
          maskTextSelector: "*",
          maskAttributeFn: maskTextAttribute,
          // API responses carry client names and phone numbers.
          recordHeaders: false,
          recordBody: false
        },
        disable_surveys: true,
        before_send: redactEvent
      });
      posthog = ph;
      status = "ready";
      run((p) => rememberAdClick(p, landingSearch));
      pending.splice(0).forEach(run);
    })
    .catch(() => {
      // Blocked or broken SDK — the widget runs untracked.
      status = "off";
      pending.length = 0;
    });
}

export function track(event, properties = {}) {
  safely((ph) => ph.capture(event, properties));
}

/**
 * For an event fired right before the page navigates away (the Telr redirect):
 * sent at once by beacon rather than waiting for the next batch.
 */
export function trackBeforeLeaving(event, properties = {}) {
  safely((ph) => ph.capture(event, properties, { send_instantly: true, transport: "sendBeacon" }));
}

/**
 * Track at most once per browser for a given key — result pages (payment done,
 * booking confirmation) are reloaded and revisited, but the conversion
 * happened once.
 */
export function trackOnce(key, event, properties = {}) {
  if (status === "off") return;
  const storageKey = `ph_once_${key}`;
  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, "1");
  } catch (_err) {
    // Private mode / full quota — a rare double count beats a missed conversion.
  }
  track(event, properties);
}

// Who made the invoice the result page is showing. Only "widget" is a sale the
// widget won: "DASHBOARD" is Pay Now on a booking the club made at the desk, and
// anything else is a pay link the club sent from Partners — the widget only
// collected the money.
const INVOICE_ORIGINS = { "widget-v2": "widget", DASHBOARD: "pay_now" };

/**
 * Shared properties for the checkout result pages (Telr charge, package/voucher
 * confirmation). Those pages are slug-less and can be the visitor's very first
 * page — a pay link opened from WhatsApp — so the club comes from the invoice.
 * `value` + `currency` are what the Meta / Google conversion forwards read.
 */
export function invoiceProperties(data) {
  const invoice = data?.invoice || {};
  const origin = INVOICE_ORIGINS[invoice.source] || "pay_link";
  return {
    saas_id: invoice.saasId || data?.payment?.saasId || null,
    invoice_id: invoice._id || null,
    invoice_kind: (invoice.bookishs || []).length > 0 ? "booking" : "sale",
    origin,
    from_widget: origin === "widget",
    value: parseFloat(data?.payment?.amount ?? invoice.total) || 0,
    currency: String(data?.payment?.currency || data?.saas?.currency || "AED").toUpperCase()
  };
}

/**
 * The event for an invoice that is now paid. booking_confirmed / plan_purchased
 * are conversions — what funnels and ad platforms count — so they are kept for
 * sales the widget made; settling the club's own booking or paying its link is
 * tracked under its own name.
 */
export function paidInvoiceEvent(props) {
  if (props.origin === "pay_now") return "booking_settled";
  if (props.origin === "pay_link") return "pay_link_paid";
  return props.invoice_kind === "booking" ? "booking_confirmed" : "plan_purchased";
}

/**
 * The club the visitor is browsing. Persisted, so slug-less pages (Telr
 * checkout, confirmation) still attribute their events to the club.
 */
export function setClub({ saasId, slug, name }) {
  if (!saasId) return;
  safely((ph) => ph.register({ saas_id: saasId, club_slug: slug || null, club_name: name || null }));
}

export function identifyUser({ userId, role, clientId, saasId }) {
  if (!userId) return;
  // Slug-less pages (Telr, confirmation) know the user but not the club — leave
  // the person's last known club in place rather than blanking it.
  const personProps = { role: role || "client" };
  if (clientId) personProps.client_id = clientId;
  if (saasId) personProps.saas_id = saasId;
  safely((ph) => {
    ph.identify(userId, personProps);
    ph.register({ role: personProps.role });
  });
}

/** Sign-out: start a fresh anonymous visitor so the next person on the device isn't merged in. */
export function resetUser() {
  safely((ph) => ph.reset());
}
